import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import {
    AssetDetailsResponse,
    CreateAssetWithAddressRequest,
    CreateAssetWithAddressResponse,
    UpdateAssetDetailsRequest,
} from './assetApiTypes';

export interface UploadImagesResponse {
    id: string;
    additionalImages: string[];
    uploadedUrls: string[];
    failedFiles: Array<{ filename: string, error: string }>;
}

export interface DeleteImageResponse {
    id: string;
    additionalImages: string[];
}

export interface ReplaceFeaturedImageResponse {
    id: string;
    featuredImage: string;
}

export const assetApiSlice = createApi({
    reducerPath: 'assetApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: ['assetDetails'],
    endpoints: (builder) => ({
        getAssetDetails: builder.query<AssetDetailsResponse, { assetId: string }>({
            query: ({ assetId }) => ({
                url: `/asset/${assetId}/details`,
                method: 'GET',
            }),
            providesTags: ['assetDetails'],
        }),
        createAssetWithAddress: builder.mutation<CreateAssetWithAddressResponse, CreateAssetWithAddressRequest>({
            query: ({ featuredImage, additionalImages, ...jsonPayload }) => {
                const formData = new FormData();
                formData.append('data', JSON.stringify(jsonPayload));
                if (featuredImage) {
                    formData.append('featuredImage', featuredImage);
                }
                if (additionalImages) {
                    additionalImages.forEach((file) => formData.append('additionalImages', file));
                }
                return {
                    url: '/asset',
                    method: 'POST',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
            },
            invalidatesTags: ['assetDetails'],
        }),
        updateAssetDetails: builder.mutation<void, { assetId: string, body: UpdateAssetDetailsRequest }>({
            query: ({ assetId, body }) => ({
                url: `/asset/${assetId}`,
                method: 'PUT',
                data: body,
            }),
            invalidatesTags: ['assetDetails'],
        }),
        uploadAssetImages: builder.mutation<UploadImagesResponse, { assetId: string, files: File[] }>({
            query: ({ assetId, files }) => {
                const formData = new FormData();
                files.forEach((file) => formData.append('images', file));
                return {
                    url: `/asset/${assetId}/images`,
                    method: 'POST',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
            },
            invalidatesTags: ['assetDetails'],
        }),
        deleteAssetImage: builder.mutation<DeleteImageResponse, { assetId: string, imageUrl: string }>({
            query: ({ assetId, imageUrl }) => ({
                url: `/asset/${assetId}/images`,
                method: 'DELETE',
                data: { imageUrl },
            }),
            invalidatesTags: ['assetDetails'],
        }),
        deleteAsset: builder.mutation<void, { assetId: string }>({
            query: ({ assetId }) => ({
                url: `/asset/${assetId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['assetDetails'],
        }),
        replaceFeaturedImage: builder.mutation<ReplaceFeaturedImageResponse, { assetId: string, file: File }>({
            query: ({ assetId, file }) => {
                const formData = new FormData();
                formData.append('image', file);
                return {
                    url: `/asset/${assetId}/featured-image`,
                    method: 'PUT',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
            },
            invalidatesTags: ['assetDetails'],
        }),
    }),
});

export const {
    useGetAssetDetailsQuery,
    useCreateAssetWithAddressMutation,
    useUpdateAssetDetailsMutation,
    useDeleteAssetMutation,
    useUploadAssetImagesMutation,
    useDeleteAssetImageMutation,
    useReplaceFeaturedImageMutation,
} = assetApiSlice;
